<template>
  <div id="app">
    <el-config-provider>
      <el-container class="app-container">
        <NavBar v-if="showNavbar" />
        
        <!-- 全局侧边栏（移动端） -->
        <div 
          v-if="showNavbar" 
          class="global-sidebar fixed-sidebar" 
          :class="{ 'mobile-sidebar-show': sidebarVisible }"
        >
          <el-menu
            :default-active="activeIndex"
            class="el-menu-vertical"
            background-color="#f5f5f5"
            text-color="#333"
            active-text-color="#d32f2f"
            router
            @select="handleMenuSelect"
          >
            <!-- 订单相关菜单 -->
            <el-menu-item :index="isAdmin ? '/admin/orders' : '/user/orders'">
              <span>订单列表</span>
            </el-menu-item>
            <el-menu-item index="/orderprocessing">
              <span>订单处理</span>
            </el-menu-item>
            <el-menu-item v-if="isAdmin" index="/model">
              <span>型号管理</span>
            </el-menu-item>
            <el-menu-item index="/unit">
              <span>验配单位管理</span>
            </el-menu-item>
            <el-sub-menu index="2">
              <template #title>
                <span>下单</span>
              </template>
              <el-menu-item index="/supermaster">
                <span>散单-超主点</span>
              </el-menu-item>
              <el-menu-item index="/astigmatism">
                <span>散单-其他</span>
              </el-menu-item>
              <el-menu-item index="/bulkorders">
                <span>批量-库存片</span>
              </el-menu-item>
            </el-sub-menu>
            
            <!-- 分隔线（仅在移动端显示） -->
            <el-divider v-if="isMobileDevice" />
            
            <!-- 顶部导航栏菜单项（仅在移动端显示） -->
            <el-menu-item v-if="isMobileDevice" :index="isAdmin ? '/admin/users' : '/user/info'">
              <span>{{ isAdmin ? '用户管理' : '个人信息' }}</span>
            </el-menu-item>
            <el-menu-item v-if="isMobileDevice" index="/mail">
              <span>快递查询</span>
            </el-menu-item>
            <el-menu-item v-if="isMobileDevice && isAdmin" index="/admin/analyze">
              <span>用户状况分析</span>
            </el-menu-item>
            <el-menu-item v-if="isMobileDevice && isAdmin" index="/ad">
              <span>广告控制面板</span>
            </el-menu-item>
          </el-menu>
        </div>
        
        <!-- 遮罩层 -->
        <div 
          v-if="showNavbar && sidebarVisible && isMobileDevice" 
          class="sidebar-overlay" 
          @click="sidebarVisible = false"
        ></div>
        
        <el-main :class="{ 'with-navbar': showNavbar, 'with-bottom-nav': showNavbar, 'with-sidebar': showNavbar && !isMobileDevice }">
          <router-view />
        </el-main>
        <BottomNavBar v-if="showNavbar" />
      </el-container>
    </el-config-provider>
  </div>
</template>

<script setup>
import { ElConfigProvider } from 'element-plus'
import NavBar from '@/components/NavBar.vue'
import BottomNavBar from '@/components/BottomNavBar.vue'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const sidebarVisible = ref(false)
const isMobileDevice = ref(window.innerWidth <= 768)
const activeIndex = ref('')

const showNavbar = computed(() => {
  // 在登录和注册页面不显示导航栏
  return !['Login', 'Register'].includes(route.name)
})

// 从localStorage获取用户角色信息
const isAdmin = computed(() => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
  return userInfo.role === 'ADMIN'
})

// 设置默认活动菜单项
const defaultRoute = computed(() => {
  return isAdmin.value ? '/admin/orders' : '/user/orders'
})

// 根据路由路径更新activeIndex
watch(() => route.path, (newPath) => {
  if (newPath === '/order') {
    activeIndex.value = defaultRoute.value
  } else {
    activeIndex.value = newPath
  }
}, { immediate: true })

// 监听窗口大小变化
const handleResize = () => {
  isMobileDevice.value = window.innerWidth <= 768
  if (window.innerWidth > 768) {
    sidebarVisible.value = false // 当屏幕变大时重置状态
  }
}

// 监听底部导航栏的菜单点击事件
const handleToggleSidebar = () => {
  if (isMobileDevice.value) {
    sidebarVisible.value = !sidebarVisible.value
  }
}

// 处理菜单选择
const handleMenuSelect = () => {
  if (isMobileDevice.value) {
    sidebarVisible.value = false // 移动端点击菜单后收起侧边栏
  }
}

// 添加移动端视口设置
onMounted(() => {
  // 确保viewport meta标签存在且配置正确
  const viewportMeta = document.querySelector('meta[name="viewport"]')
  if (!viewportMeta) {
    const meta = document.createElement('meta')
    meta.name = 'viewport'
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    document.getElementsByTagName('head')[0].appendChild(meta)
  } else {
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
  }
  
  // 添加事件监听
  window.addEventListener('resize', handleResize)
  window.addEventListener('toggle-sidebar', handleToggleSidebar)
  
  // 设置默认活动页面
  activeIndex.value = defaultRoute.value
})

// 组件卸载时移除事件监听
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('toggle-sidebar', handleToggleSidebar)
})
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overscroll-behavior: none; /* 防止页面弹性滚动 */
}

#app {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB',
    'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  padding: 0;
  min-height: 100vh;
  background-color: #f5f7fa;
  position: relative;
  overflow-x: hidden; /* 防止水平滚动 */
}

body {
  margin: 0;
  padding: 0;
  touch-action: manipulation; /* 改善移动端点击体验 */
}

.app-container {
  min-height: 100vh;
}

.el-main {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: 100vh;
}

.el-main.with-navbar {
  padding-top: 80px;
}
  
.el-main.with-bottom-nav {
  padding-bottom: 80px;
}

/* 全局侧边栏样式 */
.global-sidebar {
  position: fixed;
  top: 60px;
  left: 0;
  height: calc(100vh - 60px);
  z-index: 1000;
  width: 200px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  background-color: #f5f5f5;
  transition: transform 0.3s ease;
  display: none; /* 默认隐藏，仅在桌面端显示 */
}

.global-sidebar .el-menu-vertical {
  height: 100%;
  border-right: none;
  overflow-y: auto;
}

/* 遮罩层样式 */
.sidebar-overlay {
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 60px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

/* 桌面端显示侧边栏 */
@media screen and (min-width: 769px) {
  .global-sidebar {
    display: block;
  }
  
  .el-main.with-sidebar {
    margin-left: 200px;
    width: calc(100% - 200px);
  }
}

/* 移动端响应式设计 */
@media screen and (max-width: 768px) {
  .global-sidebar {
    transform: translateX(-100%);
    width: 85%;
    max-width: 250px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    z-index: 2001;
    height: calc(100vh - 60px - 60px);
    display: block;
  }
  
  .global-sidebar.mobile-sidebar-show {
    transform: translateX(0);
  }
  
  .sidebar-overlay {
    display: block;
  }
  
  .el-menu-vertical :deep(.el-menu-item) {
    padding: 0 20px !important;
    height: 48px;
    line-height: 48px;
  }
  
  .el-menu-vertical :deep(.el-divider) {
    margin: 12px 16px;
    border-color: #e4e7ed;
  }
}

@media screen and (max-width: 910px) and (min-width: 769px) {
  .global-sidebar {
    width: 140px;
  }
  
  .el-main.with-sidebar {
    margin-left: 140px;
    width: calc(100% - 140px);
  }
}

/* Element Plus 样式覆盖 */
.el-menu {
  border-bottom: none;
}

.el-card {
  margin-bottom: 20px;
  transition: all 0.3s ease; /* 添加过渡动画 */
}

.el-table {
  margin-top: 20px;
}

.el-form {
  max-width: 500px;
  margin: 0 auto;
}

.el-dialog {
  border-radius: 8px;
}

.el-button {
  border-radius: 4px;
}

.el-input__inner {
  border-radius: 4px;
}

.el-select {
  width: 100%;
}

.el-date-editor {
  width: 100%;
}

/* 响应式布局 */
@media screen and (max-width: 768px) {
  .el-main {
    padding: 10px;
  }
  
  .el-main.with-navbar {
    padding-top: 70px;
  }
  
  .el-main.with-bottom-nav {
    padding-bottom: 70px;
  }
  
  .el-form {
    max-width: 100%;
    padding: 0 10px;
  }
  
  .el-dialog {
    width: 85% !important; /* 移动端对话框宽度 */
  }
  
  .el-card {
    margin-bottom: 15px;
  }
  
  /* 表格滚动 */
  .el-table {
    width: 100%;
    overflow-x: auto;
  }
  
  /* 改善移动端按钮样式 */
  .el-button {
    padding: 10px 15px;
  }
  
  /* 移动端表单项间距 */
  .el-form-item {
    margin-bottom: 15px;
  }
}
</style>
